/* eslint-disable node/prefer-global/process */
import type { CarsListSuccess } from '@autobid/strapi-integration/typescript/auction/carsList'
import type { SearchCarsApiFilters } from '@autobid/ui/composables/car/data/useSearchCars'
import type {
  ItemListFiltersStateKey,
} from '@autobid/ui/composables/carsList/useFilter'
import type { SortByItem } from '@autobid/ui/types/components/CarsList'
import type { UseQueryOptions } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useAuctionCarsSocket } from '@autobid/ui/composables/car/sockets/useAuctionCarsSocket'
import {
  useCarsListUrlFilter,
} from '@autobid/ui/composables/carsList/useFilter'
import { CARS_LIST_DEFAULT_SORTING_TYPE } from '@autobid/ui/constants/carsList/CARS_LIST_DEFAULT_SORTING_TYPE'
import { prepareMultiErrorCause } from '@autobid/ui/utils/auctions/prepareMultiErrorCause'
import { generateImageSizesFromObject } from '@autobid/ui/utils/car/generateImageSizes'
import { getSortBy } from '@autobid/ui/utils/prepareCarsListFiltersUtils'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, unref } from 'vue'

// #region constants
const CACHE_TIME = 1000 * 60 * 60

export const LIST_OF_SUPPORTED_CARS_LIST_FILTERS: ItemListFiltersStateKey[] = [
  'currentPage',
  'sortingType',
  'pageType',
  'e174',
  'category',
  'manufacturerModel',
  'e17',
  'e223',
  'stage',
  'supplierIds',
]
// #endregion

// #region types
export type AuctionCarsApiFilters = Pick<
  SearchCarsApiFilters,
  | 'pageSize'
  | 'pageNumber'
  | 'lang'
  | 'sortBy'
  | 'e174'
  | 'typeCategory'
  | 'manufacturerModel'
  | 'e17'
  | 'e223'
> & {
  stage?: 'online' | 'all' | 'finished' | undefined
  auctionId: number
  supplierIds?: number[]
}
// #endregion

// #region internal functions
function prepareCarData(data: CarsListSuccess['data']['items']) {
  const items = data.items.map((car) => {
    return {
      ...car,
      imageGroups: generateImageSizesFromObject('hd', car.imageGroups),
    }
  })

  return {
    ...data,
    items,
  }
}
// #endregion

export const auctionCarsQueryKey = {
  all: () => ['cars-list'] as const,
  byAuctionId: (auctionId: number) => ['cars-list', auctionId] as const,
  filtered: (auctionId: number, filters: AuctionCarsApiFilters) => ['cars-list', auctionId, filters] as const,
}

export function useAuctionCars(auctionId: Ref<number>, pageSize: number, options?: UseQueryOptions<CarsListSuccess['data']['items'] | null, Error>) {
  const { locale } = useI18n()
  const { isAuthed, setPageRedirectData } = useAutobidAuth()
  const { $trpc } = useNuxtApp()
  const { init: initSockets } = useAuctionCarsSocket()
  const {
    filters: {
      currentPage,
      sortingType,
      e174,
      category,
      manufacturerModel,
      e17,
      e223,
      stage,
      supplierIds,
    },
  } = useCarsListUrlFilter()

  const filters = computed((): AuctionCarsApiFilters => {
    const [sortName, sortOrder] = (
      sortingType.value || CARS_LIST_DEFAULT_SORTING_TYPE
    ).split('-')

    return {
      pageSize,
      pageNumber: (currentPage.value || 1) - 1,
      lang: locale.value,
      sortBy: getSortBy(
        sortName as SortByItem['name'],
        sortOrder as SortByItem['order'],
      ),
      e174: e174.value ? true : undefined,
      typeCategory: category.value,
      manufacturerModel: manufacturerModel.value,
      e17: e17.value,
      e223: e223.value,
      stage: stage.value,
      auctionId: unref(auctionId),
      supplierIds: supplierIds.value,
    }
  })
  const queryKey = computed(() =>
    auctionCarsQueryKey.filtered(
      unref(auctionId),
      unref(filters),
    ),
  )

  const queryFn = async (): Promise<CarsListSuccess['data']['items'] | null> => {
    const result = await $trpc.auctions.getAuctionCars.query(filters.value)

    if (!result) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No response',
      })
    }

    if ('errors' in result) {
      throw createError({
        statusCode: result.errors[0].status_code,
        statusMessage: result.errors[0].message,
        cause: prepareMultiErrorCause(result.errors),
      })
    }

    if (!result.data.items) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No response',
      })
    }

    if (result.data.items.metadata.errors.length) {
      const { error_code: statusCode, message: statusMessage }
        = result.data.items.metadata.errors[0]

      const ACCESS_ERROR_CODES = [401, 403]
      if (ACCESS_ERROR_CODES.includes(statusCode)) {
        setPageRedirectData({ redirectable: !isAuthed.value })

        throw createError({
          statusCode,
          statusMessage,
        })
      }
    }

    return prepareCarData(result.data.items)
  }

  onMounted(initSockets)

  return useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await queryFn()
      }
      catch (error) {
        const { message, code, handled } = getError(error)

        if (!handled) {
          throw createError({ statusCode: code, statusMessage: message })
        }

        return null
      }
    },
    staleTime: CACHE_TIME,
    ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    refetchOnWindowFocus: false,
    ...(options || {}),
  })
}
